import {
  type ListTasksInput,
  TASK_DUE_FILTERS,
  TASK_SORTS,
  TASK_STATUSES,
  type TaskDueFilter,
  type TaskScope,
  type TaskSort,
  type TaskStatus,
} from '@fos/shared/contracts/work';

/**
 * Filtry listy i tablicy zadań w adresie strony (po polsku, bez ogonków), żeby dało się podlinkować
 * np. zadania klienta: `/zadania?klient=<id>` albo widok `/zadania?widok=lista&termin=po-terminie`.
 */

export type TaskView = 'lista' | 'tablica';

/**
 * Wykonawca: domyślnie wg przełącznika Moje / Zespół; „wszyscy” = cały mój zakres, „brak” = bez wykonawcy,
 * „zlecone” = zadania, które dodałem i przypisałem innym („Zlecone przeze mnie”, 10.10.2026).
 */
export type AssigneeFilter =
  | { kind: 'scope' }
  | { kind: 'delegated' }
  | { kind: 'all' }
  | { kind: 'me' }
  | { kind: 'none' }
  | { kind: 'user'; userId: string };

export interface TaskFilterState {
  view: TaskView | null;
  clientId: string | null | undefined;
  projectId: string | null | undefined;
  serviceId: string | undefined;
  assignee: AssigneeFilter;
  due: TaskDueFilter[];
  status: TaskStatus[];
  q: string;
  archived: boolean;
  sort: TaskSort;
  toolbar: boolean;
}

const DUE_PARAM: Record<TaskDueFilter, string> = {
  overdue: 'po-terminie',
  today: 'dzis',
  week: 'tydzien',
  none: 'bez',
};
const DUE_FROM_PARAM = Object.fromEntries(Object.entries(DUE_PARAM).map(([k, v]) => [v, k])) as Record<
  string,
  TaskDueFilter
>;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NONE = 'brak';
const DELEGATED = 'zlecone';

function idOrNone(raw: string | null): string | null | undefined {
  if (raw === null || raw === '') return undefined;
  if (raw === NONE) return null;
  return UUID.test(raw) ? raw : undefined;
}

export function parseTaskFilters(params: URLSearchParams): TaskFilterState {
  const view = params.get('widok');
  const assigneeRaw = params.get('wykonawca');
  let assignee: AssigneeFilter = { kind: 'scope' };
  if (assigneeRaw === 'wszyscy') assignee = { kind: 'all' };
  else if (assigneeRaw === 'ja') assignee = { kind: 'me' };
  else if (assigneeRaw === DELEGATED) assignee = { kind: 'delegated' };
  else if (assigneeRaw === NONE) assignee = { kind: 'none' };
  else if (assigneeRaw && UUID.test(assigneeRaw)) assignee = { kind: 'user', userId: assigneeRaw };
  const list = (raw: string | null) => (raw ? raw.split(',').filter(Boolean) : []);
  const sort = params.get('sort');
  return {
    view: view === 'lista' || view === 'tablica' ? view : null,
    clientId: idOrNone(params.get('klient')),
    projectId: idOrNone(params.get('projekt')),
    serviceId: idOrNone(params.get('usluga')) ?? undefined,
    assignee,
    due: list(params.get('termin'))
      .map((d) => DUE_FROM_PARAM[d])
      .filter((d): d is TaskDueFilter => d !== undefined && (TASK_DUE_FILTERS as readonly string[]).includes(d)),
    status: list(params.get('status')).filter((s): s is TaskStatus => (TASK_STATUSES as readonly string[]).includes(s)),
    q: params.get('q') ?? '',
    archived: params.get('archiwum') === '1',
    sort: sort && (TASK_SORTS as readonly string[]).includes(sort) ? (sort as TaskSort) : 'due',
    toolbar: params.get('filtry') === '1',
  };
}

/** Zapis zmian filtrów do adresu (puste wartości usuwają parametr). */
export function writeTaskFilters(params: URLSearchParams, patch: Partial<TaskFilterState>): URLSearchParams {
  const next = new URLSearchParams(params);
  const set = (key: string, value: string | null | undefined) => {
    if (value === null || value === undefined || value === '') next.delete(key);
    else next.set(key, value);
  };
  if ('view' in patch) set('widok', patch.view);
  if ('clientId' in patch) set('klient', patch.clientId === null ? NONE : patch.clientId);
  if ('projectId' in patch) set('projekt', patch.projectId === null ? NONE : patch.projectId);
  if ('serviceId' in patch) set('usluga', patch.serviceId);
  if (patch.assignee) {
    const a = patch.assignee;
    set(
      'wykonawca',
      a.kind === 'scope'
        ? null
        : a.kind === 'all'
          ? 'wszyscy'
          : a.kind === 'me'
            ? 'ja'
            : a.kind === 'delegated'
              ? DELEGATED
              : a.kind === 'none'
                ? NONE
                : a.userId,
    );
  }
  if (patch.due) set('termin', patch.due.map((d) => DUE_PARAM[d]).join(','));
  if (patch.status) set('status', patch.status.join(','));
  if ('q' in patch) set('q', patch.q?.trim() ? patch.q : null);
  if ('archived' in patch) set('archiwum', patch.archived ? '1' : null);
  if (patch.sort) set('sort', patch.sort === 'due' ? null : patch.sort);
  if ('toolbar' in patch) set('filtry', patch.toolbar ? '1' : null);
  return next;
}

/** Czy jest aktywny jakikolwiek filtr poza klientem (pokazuje pasek filtrów i „Wyczyść”). */
export function hasExtraFilters(f: TaskFilterState): boolean {
  return (
    f.projectId !== undefined ||
    f.serviceId !== undefined ||
    f.assignee.kind !== 'scope' ||
    f.due.length > 0 ||
    f.status.length > 0 ||
    f.q.trim() !== '' ||
    f.archived
  );
}

/**
 * Wejście usług list/tablicy z filtrów i przełącznika „Moje / Zespół”. „Moje” na ekranie Zadania = przypisane do mnie
 * + dodane przeze mnie i przypisane innym (`delegated: 'include'`, 10.10.2026; Dziś zostaje przy samych moich).
 */
export function toListInput(
  f: TaskFilterState,
  scope: TaskScope,
  me: string,
): Omit<NonNullable<ListTasksInput>, 'sort' | 'limit'> {
  const assignee =
    f.assignee.kind === 'scope'
      ? scope === 'mine'
        ? { scope, delegated: 'include' as const }
        : { scope }
      : f.assignee.kind === 'delegated'
        ? { scope: 'team' as const, delegated: 'only' as const }
        : f.assignee.kind === 'all'
          ? { scope: 'team' as const }
          : f.assignee.kind === 'me'
            ? { scope: 'team' as const, assigneeUserId: me }
            : f.assignee.kind === 'none'
              ? { scope: 'team' as const, assigneeUserId: null }
              : { scope: 'team' as const, assigneeUserId: f.assignee.userId };
  return {
    ...assignee,
    ...(f.clientId !== undefined ? { clientId: f.clientId } : {}),
    ...(f.projectId !== undefined ? { projectId: f.projectId } : {}),
    ...(f.serviceId !== undefined ? { serviceId: f.serviceId } : {}),
    ...(f.due.length > 0 ? { due: f.due } : {}),
    ...(f.q.trim() ? { q: f.q.trim() } : {}),
  };
}

/**
 * Liczba aktywnych filtrów (licznik przy „Filtry”, lifting 17.10.2026). `withClient` — razem z klientem
 * (telefon: klient jest w panelu filtrów, na desktopie ma własny przycisk).
 */
export function countActiveFilters(f: TaskFilterState, { withClient = false }: { withClient?: boolean } = {}): number {
  return [
    withClient && f.clientId !== undefined,
    f.projectId !== undefined,
    f.serviceId !== undefined,
    f.assignee.kind !== 'scope',
    f.due.length > 0,
    f.status.length > 0,
    f.q.trim() !== '',
    f.archived,
  ].filter(Boolean).length;
}
